import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { API_URL } from '../../../config';
import { useAuth } from '../../../context/AuthContext';

interface OrderbookData {
    date_seance: string;
    symbole: string;
    titre: string | null;
    quantite_achat: number | null;
    cours_achat: number | null;
    quantite_vente: number | null;
    cours_vente: number | null;
    cours_reference: number | null;
}

interface OrderBookWidgetProps {
    symbole: string;
    title?: string;
}

const OrderBookWidget: React.FC<OrderBookWidgetProps> = ({ symbole, title = "Carnet d'Ordres" }) => {
    const { token } = useAuth();
    const [data, setData] = useState<OrderbookData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token && symbole) {
            const fetchOrderbook = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    const response = await fetch(`${API_URL}/v1/stocks/${symbole}/orderbook`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const result = await response.json();
                        setData(result);
                    } else if (response.status === 404) {
                        setError('Carnet d\'ordres non disponible');
                    }
                } catch (err) {
                    console.error('Error fetching orderbook:', err);
                    setError('Erreur de chargement');
                } finally {
                    setLoading(false);
                }
            };
            fetchOrderbook();
        }
    }, [token, symbole]);

    const formatPrice = (val: number | null) => {
        if (val === null) return '-';
        return val.toLocaleString('fr-FR') + ' FCFA';
    };

    const formatQty = (val: number | null) => {
        if (val === null) return '-';
        return val.toLocaleString('fr-FR');
    };

    if (loading) {
        return (
            <div
                style={{
                    background: 'var(--bg-box)',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    border: '1px solid #f1f5f9',
                }}
            >
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
                    📋 {title}
                </h3>
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>Chargement...</div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div
                style={{
                    background: 'var(--bg-box)',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    border: '1px solid #f1f5f9',
                }}
            >
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
                    📋 {title}
                </h3>
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                    {error || 'Données non disponibles'}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                background: 'var(--bg-box)',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                border: '1px solid #f1f5f9',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                }}
            >
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>📋 {title}</h3>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {new Date(data.date_seance).toLocaleDateString('fr-FR')}
                </span>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                }}
            >
                {/* Buy Side (Bid) */}
                <div
                    style={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid #a7f3d0',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '12px',
                        }}
                    >
                        <ArrowDownLeft size={18} color="#059669" />
                        <span style={{ fontWeight: '600', color: '#059669', fontSize: '14px' }}>ACHAT</span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>Quantité</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>
                            {formatQty(data.quantite_achat)}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>
                            Cours Achat
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
                            {formatPrice(data.cours_achat)}
                        </div>
                    </div>
                </div>

                {/* Sell Side (Ask) */}
                <div
                    style={{
                        background: 'linear-gradient(135deg, #fff1f2 0%, #fce7e9 100%)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px solid #fecaca',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '12px',
                        }}
                    >
                        <ArrowUpRight size={18} color="#e11d48" />
                        <span style={{ fontWeight: '600', color: '#e11d48', fontSize: '14px' }}>VENTE</span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>Quantité</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>
                            {formatQty(data.quantite_vente)}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>
                            Cours Vente
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#e11d48' }}>
                            {formatPrice(data.cours_vente)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Reference Price */}
            {data.cours_reference && (
                <div
                    style={{
                        marginTop: '16px',
                        padding: '12px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        textAlign: 'center',
                    }}
                >
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Cours de Référence: </span>
                    <span style={{ fontWeight: 'bold', color: '#334155' }}>
                        {formatPrice(data.cours_reference)}
                    </span>
                </div>
            )}

            {/* Spread Info */}
            {data.cours_achat && data.cours_vente && (
                <div
                    style={{
                        marginTop: '12px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: '#94a3b8',
                    }}
                >
                    Spread: {formatPrice(data.cours_vente - data.cours_achat)} (
                    {(((data.cours_vente - data.cours_achat) / data.cours_achat) * 100).toFixed(2)}%)
                </div>
            )}
        </div>
    );
};

export default OrderBookWidget;

